import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAiFeatureQuotas, verifyQuestWithAudio } from '../api/dataService';
import { FIT_FACT_ITEMS } from '../constants/facts';
import TimerSessionModal from './TimerSessionModal';
import MealCaptureModal from './MealCaptureModal';
import DailyMealCheckinModal, { getLocalDayKey } from './DailyMealCheckinModal';
import { detectBrowserTimezone } from '../utils/timezone';
import { getQuestScheduleDateKey } from '../utils/questScheduleDate';
import {
  AI_FEATURE_KEYS,
  VERIFICATION_MODAL_FEATURE_KEYS,
  getFeatureQuota,
  getFirstBlockedQuota,
  getQuotaMessage,
  getRewardedUnlock,
  isQuotaBlocked
} from '../utils/aiFeatureQuotas';
import { buildRewardedUnlockCopy, REWARDED_UNLOCK_CONTEXTS } from '../utils/rewardedAds';
import { useLocale } from '../context/LocaleContext';

// --- Helper Icon Components ---
const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-yellow-400">
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
    <circle cx="12" cy="13" r="3" />
  </svg>
);

const MicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-yellow-400">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const TimerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const StopIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-red-500">
    <path d="M6 6h12v12H6z" />
  </svg>
);

const Spinner = () => (
  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
);


const VerificationModal = ({ quest, onClose, onVerificationSuccess, allQuests, initialAiFeatureQuotas = null }) => {
  const { t } = useLocale();
  // --- Component State ---
  const [isVerifying, setIsVerifying] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', type: '' }); // 'error', 'success', 'info'
  const [randomFact] = useState(() => FIT_FACT_ITEMS[Math.floor(Math.random() * FIT_FACT_ITEMS.length)]);

  // --- Audio State ---
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioSize, setAudioSize] = useState(null);
  const mediaRecorderRef = useRef(null);

  // --- Timer State ---
  const [isTimerModalOpen, setIsTimerModalOpen] = useState(false);
  const [isMealCaptureOpen, setIsMealCaptureOpen] = useState(false);
  const [isDailyMealModalOpen, setIsDailyMealModalOpen] = useState(false);
  const [dailySubmitted, setDailySubmitted] = useState(false);
  const [dailyResult, setDailyResult] = useState(null);
  const hasResolvedDailySuccessRef = React.useRef(false);
  const [aiFeatureQuotas, setAiFeatureQuotas] = useState(initialAiFeatureQuotas);
  const [isAiFeatureQuotasLoading, setIsAiFeatureQuotasLoading] = useState(!initialAiFeatureQuotas);
  const timezone = detectBrowserTimezone() || 'UTC';
  const isTimerEligible = quest && (quest.type === 'Workout' || quest.type === 'Mindfulness') && !quest.completed;
  const isMealQuest = quest?.type === 'Meal';
  const requestedFeatureKeys = useMemo(() => (
    isMealQuest
      ? VERIFICATION_MODAL_FEATURE_KEYS
      : [
          AI_FEATURE_KEYS.VOICE_NOTE_TRANSCRIBER,
          AI_FEATURE_KEYS.VOICE_NOTE_VERIFIER
        ]
  ), [isMealQuest]);

  useEffect(() => {
    if (!quest) return;
    const key = getQuestScheduleDateKey(quest) || getLocalDayKey(quest.start || quest.date, timezone);
    try {
      const userId = quest.userId || 'user';
      const stored = localStorage.getItem(`meal-day-draft:${userId}:${key}`);
      const parsed = stored ? JSON.parse(stored) : null;
      setDailySubmitted(Boolean(parsed?.sent));
    } catch {
      setDailySubmitted(false);
    }
  }, [quest, timezone]);

  const refreshAiFeatureQuotas = useCallback(() => {
    if (!quest) return Promise.resolve();
    setIsAiFeatureQuotasLoading(true);
    return getAiFeatureQuotas({ features: requestedFeatureKeys, force: true })
      .then((response) => {
        setAiFeatureQuotas(response.data || null);
      })
      .catch((err) => {
        console.warn('[VerificationModal] Failed to refresh AI feature quotas', err);
      })
      .finally(() => {
        setIsAiFeatureQuotasLoading(false);
      });
  }, [quest, requestedFeatureKeys]);

  useEffect(() => {
    if (!quest) return undefined;
    let cancelled = false;
    setAiFeatureQuotas(initialAiFeatureQuotas);
    setIsAiFeatureQuotasLoading(!initialAiFeatureQuotas);

    getAiFeatureQuotas({ features: requestedFeatureKeys })
      .then((response) => {
        if (!cancelled) {
          setAiFeatureQuotas(response.data || null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[VerificationModal] Failed to fetch AI feature quotas', err);
          setAiFeatureQuotas(initialAiFeatureQuotas);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsAiFeatureQuotasLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialAiFeatureQuotas, quest, requestedFeatureKeys]);

  useEffect(() => {
    if (!quest) return undefined;
    const handleRefresh = () => {
      refreshAiFeatureQuotas();
    };
    window.addEventListener('forza:aiFeatureQuotasRefresh', handleRefresh);
    return () => window.removeEventListener('forza:aiFeatureQuotasRefresh', handleRefresh);
  }, [quest, refreshAiFeatureQuotas]);

  const mealPhotoQuota = getFeatureQuota(aiFeatureQuotas, AI_FEATURE_KEYS.MEAL_PHOTOS_VERIFIER);
  const mealPhotoBlocked = isQuotaBlocked(mealPhotoQuota);
  const mealPhotoQuotaMessage = getQuotaMessage(mealPhotoQuota);
  const mealPhotoUpgradeRequired = mealPhotoBlocked && mealPhotoQuota?.tier === 'free-tier';
  const blockedVoiceQuota = getFirstBlockedQuota(aiFeatureQuotas, [
    AI_FEATURE_KEYS.VOICE_NOTE_TRANSCRIBER,
    AI_FEATURE_KEYS.VOICE_NOTE_VERIFIER
  ]);
  const voiceNoteBlocked = isQuotaBlocked(blockedVoiceQuota);
  const voiceNoteQuotaMessage = getQuotaMessage(blockedVoiceQuota);
  const voiceNoteRewardedUnlock = getRewardedUnlock(blockedVoiceQuota);
  const canStartVoiceNoteRewardedUnlock = Boolean(voiceNoteRewardedUnlock?.canStart);
  const voiceNoteUnlockCopy = buildRewardedUnlockCopy(REWARDED_UNLOCK_CONTEXTS.VOICE_NOTE, voiceNoteRewardedUnlock, t);
  const isVoiceQuotaPending = isAiFeatureQuotasLoading && !aiFeatureQuotas;
  const isMealPhotoQuotaPending = isMealQuest && isAiFeatureQuotasLoading && !aiFeatureQuotas;
  const voiceNoteUnavailable = isVoiceQuotaPending || voiceNoteBlocked;
  const mealPhotoUnavailable = isMealPhotoQuotaPending || mealPhotoBlocked;

  // --- Handlers ---
  const handleStartRecording = () => {
    if (isVoiceQuotaPending) {
      setFeedback({ message: t('verificationModal.checkingVoiceAvailability', 'Checking voice note availability...'), type: 'info' });
      return;
    }
    if (voiceNoteBlocked) {
      setFeedback({ message: voiceNoteQuotaMessage, type: 'info' });
      return;
    }
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;
        const audioChunks = [];

        mediaRecorder.ondataavailable = event => {
          audioChunks.push(event.data);
        };

        mediaRecorder.onstop = () => {
          const newAudioBlob = new Blob(audioChunks, { type: 'audio/webm' });
          setAudioBlob(newAudioBlob);
          setAudioSize(newAudioBlob.size);
          stream.getTracks().forEach(track => track.stop()); // Release microphone
        };

        // Reset other verification types
        setFeedback({ message: '', type: '' });

        mediaRecorder.start();
        setIsRecording(true);
      })
      .catch(err => {
        console.error('Error accessing microphone:', err);
        setFeedback({ message: t('verificationModal.microphoneDenied', 'Microphone access denied. Please enable it in your browser settings.'), type: 'error' });
      });
  };

  const handleVoiceNoteRewardedUnlock = () => {
    const showRewardedAd = window?.forzaNative?.showRewardedAd;
    if (typeof showRewardedAd !== 'function') {
      setFeedback({ message: voiceNoteUnlockCopy.fallbackMessage, type: 'info' });
      return;
    }
    showRewardedAd('voiceNoteExtraUse');
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleTimerComplete = (payload) => {
    setIsTimerModalOpen(false);
    onVerificationSuccess(payload || null); // This closes the verification modal and refreshes data
  };

  const handleAudioVerify = async () => {
    if (isVoiceQuotaPending) {
      setFeedback({ message: t('verificationModal.checkingVoiceAvailability', 'Checking voice note availability...'), type: 'info' });
      return;
    }
    if (voiceNoteBlocked) {
      setFeedback({ message: voiceNoteQuotaMessage, type: 'info' });
      return;
    }
    if (!audioBlob) {
      setFeedback({ message: t('verificationModal.recordVoicePrompt', 'Please record your voice for verification.'), type: 'error' });
      return;
    }
    setIsVerifying(true);
    setFeedback({ message: t('verificationModal.verifying', 'Verifying with AI...'), type: 'info' });

    try {
      const audioFile = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });

      // Enforce minimum display time for the fact (e.g., 4 seconds)
      const minWait = new Promise(resolve => setTimeout(resolve, 4000));
      const verifyPromise = verifyQuestWithAudio(quest._id, audioFile);

      const result = await Promise.all([minWait, verifyPromise]);
      const response = result[1];

      refreshAiFeatureQuotas();
      setFeedback({ message: response.data.message, type: 'success' });
      if (response.data.leveledUp) {
        setTimeout(() => {
          setFeedback({
            message: t('verificationModal.levelUp', "Level Up! You've reached Level {level}!", { level: response.data.newLevel }),
            type: 'success'
          });
        }, 1500);
      }
      // Notify parent on success and close after a delay
      setTimeout(() => {
        onVerificationSuccess(response.data);
      }, 3000);
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message || t('verificationModal.failed', 'Verification failed. Please try again.');
      refreshAiFeatureQuotas();
      setFeedback({ message, type: status === 429 || status === 403 ? 'info' : 'error' });
      setIsVerifying(false); // Re-enable button on failure
    }
  };

  const handleMealCaptureSuccess = (payload) => {
    setIsMealCaptureOpen(false);
    refreshAiFeatureQuotas();
    const leveledUp = Boolean(payload?.leveledUp);
    const message = payload?.message || t('verificationModal.mealSubmitted', 'Meal verification submitted.');
    setFeedback({ message, type: 'success' });
    // mimic previous behavior: close modal after delay
    setTimeout(() => {
      onVerificationSuccess(payload || { leveledUp });
    }, 2000);
  };

  const handleDailySuccess = (payload) => {
    if (hasResolvedDailySuccessRef.current) {
      return;
    }
    hasResolvedDailySuccessRef.current = true;
    setIsDailyMealModalOpen(false);
    setDailySubmitted(true);
    setDailyResult(payload || null);
    setFeedback({ message: payload?.message || t('verificationModal.dailySubmitted', 'Daily meal submission recorded.'), type: 'success' });
    onVerificationSuccess?.(payload || null);
  };

  const handleClose = () => {
    if (dailyResult && !hasResolvedDailySuccessRef.current) {
      hasResolvedDailySuccessRef.current = true;
      onVerificationSuccess?.(dailyResult);
    }
    onClose();
  };

  const buttonCount = (isMealQuest ? 2 : 0) + 1 + (isTimerEligible ? 1 : 0);
  const gridColumnsClass =
    buttonCount >= 4 ? 'grid-cols-2 md:grid-cols-4' : buttonCount === 3 ? 'grid-cols-3' : buttonCount === 2 ? 'grid-cols-2' : 'grid-cols-1';

  const lockVerifications = isMealQuest && dailySubmitted;
  const isSubmitDisabled = isVerifying || isRecording || lockVerifications;
  const hasAudio = audioBlob !== null;

  return (
    <>
      <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleClose} aria-modal="true" role="dialog">
        <div className="bg-slate-900/60 rounded-[2rem] p-6 shadow-2xl border border-white/10 backdrop-blur-2xl w-full max-w-sm relative" onClick={(e) => e.stopPropagation()}>

          {isVerifying ? (
            <div className="flex flex-col items-center justify-center py-8 animate-fade-in">
              <div className="mb-6 relative">
                <div className="absolute inset-0 bg-blue-500 blur-xl opacity-20 rounded-full animate-pulse"></div>
                <Spinner />
              </div>
              <h3 className="text-xl font-bold text-white mb-4 text-center">{t('verificationModal.verifyingQuestTitle', 'Verifying Quest...')}</h3>

              <div className="bg-slate-950/50 border border-white/5 rounded-xl p-4 w-full shadow-inner">
                <div className="flex items-center mb-2">
                  <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider">{t('verificationModal.didYouKnow', 'Did You Know?')}</span>
                </div>
                <p className="text-gray-300 text-sm italic leading-relaxed">
                  "{randomFact?.text || ''}"
                </p>
                {randomFact ? (
                  <div className="mt-3">
                    <Link
                      to={`/health-sources?fact=${encodeURIComponent(randomFact.id)}`}
                      className="text-[11px] font-semibold text-cyan-300 hover:text-cyan-200"
                    >
                      {t('verificationModal.seeSources', 'See sources')}
                    </Link>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('verificationModal.title', 'Verify Quest')}: <span className="font-semibold">{quest.name}</span>
              </h2>
              <p className="text-gray-400 mb-6 text-sm">
                {isMealQuest
                  ? t('verificationModal.mealDescription', 'Choose your verification method. Capture your meal or send a voice note.')
                  : t('verificationModal.standardDescription', 'Choose your verification method. You can record a voice note or start a guided timer.')}
              </p>

              <div className={`grid ${gridColumnsClass} gap-4 mb-4`}>
                {/* --- Meal Capture Button (Meal quests only) --- */}
                {isMealQuest && (
                  <button
                    onClick={() => setIsMealCaptureOpen(true)}
                    disabled={isSubmitDisabled || mealPhotoUnavailable}
                    className={`flex flex-col items-center justify-center space-y-2 p-4 bg-slate-950/50 rounded-xl border border-white/10 shadow-inner hover:bg-slate-800/50 hover:border-cyan-400/50 transition-colors ${(isSubmitDisabled || mealPhotoUnavailable) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <CameraIcon />
                    <span className="text-white font-medium text-sm text-center">{t('verificationModal.startMealCapture', 'Start Meal Capture')}</span>
                    {mealPhotoUnavailable && (
                      <span className="text-[11px] text-amber-300 text-center">
                        {isMealPhotoQuotaPending
                          ? t('verificationModal.checkingShort', 'Checking...')
                          : mealPhotoUpgradeRequired
                            ? t('verificationModal.ultraMembers', 'Ultra members')
                            : t('verificationModal.unavailable', 'Unavailable')}
                      </span>
                    )}
                  </button>
                )}
                {isMealQuest && (
                  <button
                    onClick={() => setIsDailyMealModalOpen(true)}
                    disabled={isSubmitDisabled || dailySubmitted}
                    className={`flex flex-col items-center justify-center space-y-2 p-4 bg-slate-950/50 rounded-xl shadow-inner transition-colors ${dailySubmitted ? 'border border-green-500/50 opacity-60 cursor-not-allowed' : 'border border-white/10 hover:bg-slate-800/50 hover:border-cyan-400/50'} ${isSubmitDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <TimerIcon />
                    <span className="text-white font-medium text-sm text-center">{t('verificationModal.completeAllMeals', 'Complete all meals today')}</span>
                    {dailySubmitted && <span className="text-[11px] text-green-400">{t('verificationModal.alreadySentToday', 'Already sent for today')}</span>}
                  </button>
                )}
                {/* --- Voice Note Button --- */}
                <button onClick={isRecording ? handleStopRecording : handleStartRecording} disabled={isVerifying || lockVerifications || voiceNoteUnavailable} className={`flex flex-col items-center justify-center space-y-2 p-4 bg-slate-950/50 rounded-xl shadow-inner transition-colors ${(isVerifying || lockVerifications || voiceNoteUnavailable) ? 'opacity-50 cursor-not-allowed border-white/10' : 'hover:bg-slate-800/50 hover:border-cyan-400/50 border-white/10'} ${hasAudio ? 'border-green-500/50' : ''}`}>
                  {isRecording ? <StopIcon /> : <MicIcon />}
                  <span className="text-white font-medium text-sm">{isRecording ? t('verificationModal.recording', 'Recording...') : t('verificationModal.voiceNote', 'Voice Note')}</span>
                  {voiceNoteUnavailable && <span className="text-[11px] text-amber-300 text-center">{isVoiceQuotaPending ? t('verificationModal.checkingShort', 'Checking...') : t('verificationModal.unavailable', 'Unavailable')}</span>}
                </button>

                {/* --- Timer Button (Conditional) --- */}
                {isTimerEligible && (
                  <button onClick={() => setIsTimerModalOpen(true)} disabled={isSubmitDisabled} className={`flex flex-col items-center justify-center space-y-2 p-4 bg-slate-950/50 rounded-xl border border-white/10 shadow-inner hover:bg-slate-800/50 hover:border-cyan-400/50 transition-colors ${isSubmitDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <TimerIcon />
                    <span className="text-white font-medium text-sm">{t('verificationModal.startTimer', 'Start Timer')}</span>
                  </button>
                )}
              </div>

              {/* --- Previews and Statuses --- */}
              {(isVoiceQuotaPending || isMealPhotoQuotaPending || voiceNoteBlocked || (mealPhotoBlocked && !mealPhotoUpgradeRequired)) && (
                <div className="mb-4 rounded-lg border border-amber-400/40 bg-amber-900/30 px-3 py-2 text-sm text-amber-100">
                  {(isVoiceQuotaPending || isMealPhotoQuotaPending) && <p>{t('verificationModal.checkingFeatureAvailability', 'Checking feature availability...')}</p>}
                  {mealPhotoBlocked && !mealPhotoUpgradeRequired && <p>{mealPhotoQuotaMessage}</p>}
                  {voiceNoteBlocked && <p>{canStartVoiceNoteRewardedUnlock ? voiceNoteUnlockCopy.headline : voiceNoteQuotaMessage}</p>}
                  {voiceNoteBlocked && canStartVoiceNoteRewardedUnlock && (
                    <button
                      type="button"
                      onClick={handleVoiceNoteRewardedUnlock}
                      className="mt-3 inline-flex items-center justify-center rounded-lg border border-amber-300/50 bg-amber-300/10 px-3 py-2 text-xs font-semibold text-amber-100 transition-colors hover:border-amber-200 hover:bg-amber-300/20"
                    >
                      {voiceNoteUnlockCopy.buttonLabel}
                    </button>
                  )}
                </div>
              )}

              {audioBlob && (
                <div className="mb-4 p-3 bg-[#1e2022] rounded-lg text-center">
                  <p className="text-sm text-green-400 font-semibold">{t('verificationModal.voiceRecorded', 'Voice note recorded ({size} KB)', { size: (audioSize / 1024).toFixed(1) })}</p>
                </div>
              )}

              {/* --- Feedback Area --- */}
              {feedback.message && (
                <div className={`p-3 rounded-lg text-center mb-4 text-sm font-medium ${feedback.type === 'error' ? 'bg-red-500/20 text-red-400' : feedback.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                  {feedback.message}
                </div>
              )}

              {/* --- Action Buttons --- */}
              <div className="flex justify-end items-center space-x-4">
                <button onClick={handleClose} className="text-gray-400 hover:text-white font-medium transition-colors" disabled={isVerifying}>
                  {t('verificationModal.cancel', 'Cancel')}
                </button>
                <button
                  className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-2.5 text-sm font-bold tracking-wide text-white shadow-lg transition-all duration-200 hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleAudioVerify}
                  disabled={isSubmitDisabled || !hasAudio || voiceNoteUnavailable}
                >
                  {isVerifying ? <Spinner /> : t('verificationModal.submitVoiceNote', 'Submit Voice Note')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Render Timer Modal (stacked on top) */}
      {isTimerModalOpen && (
        <TimerSessionModal
          isOpen={isTimerModalOpen}
          onClose={() => setIsTimerModalOpen(false)}
          quest={quest}
          onComplete={handleTimerComplete}
        />
      )}
      {isMealCaptureOpen && (
        <MealCaptureModal
          isOpen={isMealCaptureOpen}
          quest={quest}
          onClose={() => setIsMealCaptureOpen(false)}
          onSuccess={handleMealCaptureSuccess}
          onQuotaStateChange={refreshAiFeatureQuotas}
        />
      )}
      {isDailyMealModalOpen && (
        <DailyMealCheckinModal
          isOpen={isDailyMealModalOpen}
          quest={quest}
          allQuests={allQuests}
          onClose={() => setIsDailyMealModalOpen(false)}
          onSuccess={handleDailySuccess}
          onSent={() => setDailySubmitted(true)}
        />
      )}
    </>
  );
};


export default VerificationModal;
